import { useTranslation } from 'react-i18next';
import React, { useState, useRef, useEffect } from 'react';
import {
  Hand,
  Info,
  Plus,
  Minus,
  ZoomIn,
  ZoomOut,
  Eraser,
  Eye,
  Hexagon,
  Target,
  Compass,
  CircleDot,
  SquareDashedBottom,
  Palette,
  Pentagon,
  Route,
  Layers,
  MapPin,
  Filter,
  Globe,
  Download,
  Crosshair,
  FlaskConical,
  Wand2,
  Home,
} from 'lucide-react';
import { useMapStore } from '../../store/map.store';
import { useMap } from '../../hooks/useMap';
import { useMapInteractions } from '../../hooks/useMapInteractions';
import { cn } from '../../utils/cn';

interface ToolButton {
  id: string;
  icon: React.ReactNode;
  label: string;
  action: () => void;
  adminOnly?: boolean;
}

type MenuId = 'controls' | 'analysis' | 'utils' | null;

/* ── Tooltip wrapper ── */
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <div className="relative group/tip inline-flex">
    {children}
    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap opacity-0 pointer-events-none group-hover/tip:opacity-100 transition-opacity duration-150 shadow-lg z-[60]" style={{ backgroundColor: 'var(--gn-bg-primary)', color: 'var(--gn-text-primary)', border: '1px solid var(--gn-border)' }}>
      {text}
    </div>
  </div>
);

export const MapControls: React.FC = () => {
  const { t } = useTranslation();
  const { currentTool, setCurrentTool, goHome } = useMapStore();
  const { setZoom, getZoom } = useMap();
  const { activateTool, deactivateTool, clearDrawings, downloadMapPng } = useMapInteractions();
  const [openMenu, setOpenMenu] = useState<MenuId>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  /* Close dropdown when clicking outside */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToolClick = (toolId: string) => {
    if (currentTool === toolId) {
      deactivateTool();
      setCurrentTool(null);
    } else {
      activateTool(toolId);
    }
    setOpenMenu(null);
  };

  const handleZoomIn = () => {
    const zoom = Math.round(getZoom());
    if (zoom < 28) setZoom(zoom + 1);
    setOpenMenu(null);
  };

  const handleZoomOut = () => {
    const zoom = Math.round(getZoom());
    if (zoom > 0) setZoom(zoom - 1);
    setOpenMenu(null);
  };

  const handleClearDrawings = () => {
    clearDrawings();
    deactivateTool();
    setCurrentTool(null);
    setOpenMenu(null);
  };

  const handleDownload = () => {
    downloadMapPng();
    setOpenMenu(null);
  };

  const toggleMenu = (menuId: MenuId) => {
    setOpenMenu((prev) => (prev === menuId ? null : menuId));
  };

  /* ─── Sub-menu definitions ─── */

  const controlTools: ToolButton[] = [
    { id: 'pan', icon: <Hand size={18} />, label: t('tools.pan'), action: () => handleToolClick('pan') },
    { id: 'info', icon: <Info size={18} />, label: t('tools.feature_info'), action: () => handleToolClick('info') },
    { id: 'zoomIn', icon: <Plus size={18} />, label: t('map.zoom_in'), action: handleZoomIn },
    { id: 'zoomOut', icon: <Minus size={18} />, label: t('map.zoom_out'), action: handleZoomOut },
    { id: 'home', icon: <Home size={18} />, label: t('tools.home'), action: () => { goHome(); setOpenMenu(null); } },
    { id: 'zoomInArea', icon: <ZoomIn size={18} />, label: t('tools.zoom_in_area'), action: () => handleToolClick('zoomInArea') },
    { id: 'zoomOutArea', icon: <ZoomOut size={18} />, label: t('tools.zoom_out_area'), action: () => handleToolClick('zoomOutArea') },
    { id: 'clearDrawings', icon: <Eraser size={18} />, label: t('tools.clear_drawings'), action: handleClearDrawings },
    { id: 'streetView', icon: <Eye size={18} />, label: t('tools.street_view'), action: () => handleToolClick('streetView') },
  ];

  const analysisTools: ToolButton[] = [
    { id: 'areaAnalysis', icon: <Hexagon size={18} />, label: t('tools.area_analysis'), action: () => handleToolClick('areaAnalysis') },
    { id: 'cannibalization', icon: <Target size={18} />, label: t('tools.cannibalization'), action: () => handleToolClick('cannibalization') },
    { id: 'catchment', icon: <Compass size={18} />, label: t('analysis.catchment'), action: () => handleToolClick('catchment') },
    { id: 'ringBuffer', icon: <CircleDot size={18} />, label: t('tools.ring_buffer'), action: () => handleToolClick('ringBuffer') },
    { id: 'polyInfo', icon: <SquareDashedBottom size={18} />, label: t('tools.info_by_polygon'), action: () => handleToolClick('polyInfo') },
  ];

  const utilsTools: ToolButton[] = [
    { id: 'editStyle', icon: <Palette size={18} />, label: t('tools.edit_style'), action: () => handleToolClick('editStyle') },
    { id: 'addPoint', icon: <MapPin size={18} />, label: t('tools.add_point'), action: () => handleToolClick('addPoint') },
    { id: 'addPolygon', icon: <Pentagon size={18} />, label: t('tools.add_polygon'), action: () => handleToolClick('addPolygon') },
    { id: 'addLine', icon: <Minus size={18} />, label: t('tools.add_line'), action: () => handleToolClick('addLine') },
    { id: 'distance', icon: <Route size={18} />, label: t('analysis.distance'), action: () => handleToolClick('distance') },
    { id: 'multiInfo', icon: <Layers size={18} />, label: t('tools.multi_info'), action: () => handleToolClick('multiInfo') },
    { id: 'filter', icon: <Filter size={18} />, label: t('tools.filter'), action: () => handleToolClick('filter'), adminOnly: true },
    { id: 'geocoder', icon: <Globe size={18} />, label: t('tools.geocoder'), action: () => handleToolClick('geocoder'), adminOnly: true },
    { id: 'download', icon: <Download size={18} />, label: t('tools.download'), action: handleDownload },
  ];

  const mainMenus: { id: MenuId; icon: React.ReactNode; label: string; tools: ToolButton[] }[] = [
    { id: 'controls', icon: <Crosshair size={20} color="currentColor" />, label: t('tools.menu_controls'), tools: controlTools },
    { id: 'analysis', icon: <FlaskConical size={20} color="currentColor" />, label: t('tools.menu_analysis'), tools: analysisTools },
    { id: 'utils', icon: <Wand2 size={20} color="currentColor" />, label: t('map.tools'), tools: utilsTools },
  ];

  /* Check if any tool in a menu group is currently active */
  const hasActiveTool = (tools: ToolButton[]) =>
    tools.some((t) => t.id === currentTool);

  const renderSubToolButton = (tool: ToolButton) => {
    const isActive = currentTool === tool.id;
    return (
      <button
        key={tool.id}
        onClick={tool.action}
        className={cn(
          'flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-xs',
          'transition-all duration-150',
          isActive
            ? 'bg-gn-secondary text-gn-primary'
            : 'text-gn-secondary hover:bg-gn-tertiary',
        )}
      >
        <span className={cn(
          'flex items-center justify-center w-7 h-7 rounded-md shrink-0',
          isActive
            ? 'bg-gn-surface'
            : 'bg-gn-tertiary',
        )}>
          {tool.icon}
        </span>
        <span className="font-medium">{tool.label}</span>
      </button>
    );
  };

  return (
    <div
      ref={toolbarRef}
      className={cn(
        'fixed z-40',
        /* Desktop: top center */
        'top-3 left-1/2 -translate-x-1/2',
        /* Mobile: below search bar & logo — pushed down */
        'max-sm:top-14',
        'transition-all duration-300 ease-out',
      )}
      role="toolbar"
      aria-label={t('tools.menu_controls')}
    >
      {/* ─── Main menu buttons (horizontal row) ─── */}
      <div className="flex items-center gap-1 px-1 h-9 bg-gn-surface backdrop-blur-sm rounded-lg shadow-lg border border-gn-light">
        {mainMenus.map((menu) => {
          const isOpen = openMenu === menu.id;
          const hasActive = hasActiveTool(menu.tools);

          return (
            <div key={menu.id} className="relative">
              <Tooltip text={menu.label}>
                <button
                  onClick={() => toggleMenu(menu.id)}
                  className="flex items-center justify-center w-9 h-7 rounded-md transition-all duration-200 focus:outline-none"
                  style={{
                    backgroundColor: isOpen ? 'var(--gn-bg-primary)' : hasActive ? 'var(--gn-bg-hover)' : 'transparent',
                  }}
                  aria-expanded={isOpen}
                >
                  <span style={{
                    display: 'inline-flex',
                    width: 20,
                    height: 20,
                    color: isOpen ? 'var(--gn-text-primary)' : 'var(--gn-text-secondary)',
                  }}>
                    {menu.icon}
                  </span>
                </button>
              </Tooltip>

              {/* ─── Dropdown sub-menu (opens downward, aligned to parent) ─── */}
              {isOpen && (
                <div
                  className={cn(
                    'absolute top-full mt-2 z-50',
                    'left-1/2 -translate-x-1/2',
                    'min-w-[200px] p-1.5',
                    'bg-gn-surface backdrop-blur-sm rounded-lg shadow-xl',
                    'border border-gn-light',
                    'animate-in fade-in slide-in-from-top-2 duration-150',
                  )}
                >
                  {/* Arrow indicator */}
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-gn-surface border-l border-t border-gn-light rotate-45" />

                  <div className="relative flex flex-col gap-0.5">
                    {menu.tools
                      .filter((t) => !t.adminOnly)
                      .map(renderSubToolButton)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
